/**
 * MHR Settings Page
 *
 * Read-only settings view for all users showing role info,
 * budget limits, current cost rates, and about information.
 */

import { useState, useEffect } from 'react';
import { User, DollarSign, Cpu, Info, Settings } from 'lucide-react';
import { PageHeader } from '../../ui';
import { useMHR } from '../contexts';
import { useAuth } from '../../contexts/AuthContext';
import * as mhrApi from '../lib/api';
import type { BudgetConfig, CostRates } from '../lib/api';

export function MHRSettings() {
  const { role } = useMHR();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<BudgetConfig | null>(null);
  const [rates, setRates] = useState<CostRates | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadData() {
      try {
        const [configData, ratesData] = await Promise.all([
          mhrApi.getBudgetConfig(),
          mhrApi.getCostRates(),
        ]);
        setConfig(configData);
        setRates(ratesData);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load settings');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader title="Settings" icon={Settings} />

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Your Role */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-2 mb-4">
          <User className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Your Role</h2>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              role === 'admin'
                ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-300'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
            }`}
          >
            {role === 'admin' ? 'Administrator' : 'User'}
          </span>
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{user?.full_name}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Your Budget Limits */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Your Budget Limits</h2>
        </div>
        {config ? (
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-sm text-gray-600 dark:text-gray-400">Per-Review Limit</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                ${config.per_review_limit?.toFixed(2) ?? '5.00'}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-sm text-gray-600 dark:text-gray-400">Monthly Site Budget</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                ${config.monthly_budget?.toFixed(2) ?? '500.00'}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Warning Threshold</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {config.monthly_warning_threshold ?? 80}%
              </span>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
              Budget limits are configured by site administrators.
            </p>
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">Budget configuration not available.</p>
        )}
      </div>

      {/* Current Cost Rates */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Cpu className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Current Cost Rates</h2>
        </div>
        {rates ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 pr-4 font-medium text-gray-500 dark:text-gray-400">Service</th>
                    <th className="text-right py-2 pl-4 font-medium text-gray-500 dark:text-gray-400">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100 dark:border-gray-700">
                    <td className="py-2.5 pr-4 text-gray-700 dark:text-gray-300">Native Extraction</td>
                    <td className="py-2.5 pl-4 text-right font-mono text-gray-900 dark:text-gray-100">
                      ${rates.nativeExtraction.toFixed(4)}/page
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100 dark:border-gray-700">
                    <td className="py-2.5 pr-4 text-gray-700 dark:text-gray-300">Textract OCR</td>
                    <td className="py-2.5 pl-4 text-right font-mono text-gray-900 dark:text-gray-100">
                      ${rates.textractOcr.toFixed(4)}/page
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100 dark:border-gray-700">
                    <td className="py-2.5 pr-4 text-gray-700 dark:text-gray-300">Claude Vision</td>
                    <td className="py-2.5 pl-4 text-right font-mono text-gray-900 dark:text-gray-100">
                      ${rates.claudeVisionVerification.toFixed(4)}/page
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100 dark:border-gray-700">
                    <td className="py-2.5 pr-4 text-gray-700 dark:text-gray-300">Input Tokens</td>
                    <td className="py-2.5 pl-4 text-right font-mono text-gray-900 dark:text-gray-100">
                      ${rates.claudeInputTokens.toFixed(2)}/1M
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100 dark:border-gray-700">
                    <td className="py-2.5 pr-4 text-gray-700 dark:text-gray-300">Output Tokens</td>
                    <td className="py-2.5 pl-4 text-right font-mono text-gray-900 dark:text-gray-100">
                      ${rates.claudeOutputTokens.toFixed(2)}/1M
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-4 text-gray-700 dark:text-gray-300">Cached Input Tokens</td>
                    <td className="py-2.5 pl-4 text-right font-mono text-gray-900 dark:text-gray-100">
                      ${rates.claudeCachedInputTokens.toFixed(2)}/1M
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
              Rates are per page for extraction, per 1M tokens for analysis.
            </p>
          </>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">Cost rates not available.</p>
        )}
      </div>

      {/* About MHR */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Info className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">About MHR</h2>
        </div>
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Medical History Review v1.0</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            3-layer document extraction (Native PDF, AWS Textract OCR, Claude Vision)
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Multi-pass criteria analysis with Claude Sonnet</p>
        </div>
      </div>
    </div>
  );
}

export default MHRSettings;
